import { useState, useEffect, useRef, useCallback } from 'react';
import { studyAPI } from '../../services/api';

// ── Durable review queue ──────────────────────────────────────────────────────
// Ratings are sent in the background (the card advances at once), but never
// dropped silently: each one is kept in localStorage until the server confirms
// it, retried with backoff, and the session shows a banner while any are stuck.
// Every rating carries a reviewId, so a retry after a lost response is applied once.

const KEY = 'upsc_review_outbox';

const read = () => {
  try { const v = JSON.parse(localStorage.getItem(KEY)); return Array.isArray(v) ? v : []; } catch (_) { return []; }
};
const write = items => { try { localStorage.setItem(KEY, JSON.stringify(items)); } catch (_) {} };
const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

// 4xx will never succeed on retry (401 is handled by the shared api instance, 408/429 are transient)
const isPermanent = err => {
  const s = err.response?.status;
  return !!s && s >= 400 && s < 500 && ![401, 408, 429].includes(s);
};

export default function useReviewOutbox({ onSynced } = {}) {
  const items    = useRef(read());
  const busy     = useRef(false);
  const timer    = useRef(null);
  const tries    = useRef(0);
  const flushRef = useRef(null);
  const synced   = useRef(onSynced);
  const [pending, setPending] = useState(items.current.length);
  const [stuck,   setStuck]   = useState(false);
  const [failed,  setFailed]  = useState('');

  synced.current = onSynced;

  const commit = useCallback(() => {
    write(items.current);
    setPending(items.current.length);
  }, []);

  const flush = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    clearTimeout(timer.current);

    while (items.current.length) {
      const item = items.current[0];
      try {
        const res = await studyAPI.submitReview(item);
        items.current.shift();
        tries.current = 0;
        setStuck(false);
        commit();
        synced.current?.(item, res.data);
      } catch (err) {
        if (isPermanent(err)) {
          items.current.shift();
          commit();
          setFailed(err.response?.data?.message || 'A rating could not be saved.');
          continue;
        }
        tries.current += 1;
        setStuck(true);
        timer.current = setTimeout(() => flushRef.current(), Math.min(30000, 2000 * 2 ** (tries.current - 1)));
        break;
      }
    }
    busy.current = false;
  }, [commit]);

  flushRef.current = flush;

  const send = useCallback(payload => {
    items.current.push({ ...payload, reviewId: newId() });
    setFailed('');
    commit();
    flush();
  }, [commit, flush]);

  const retry = useCallback(() => { tries.current = 0; flush(); }, [flush]);
  const hasPendingCard = useCallback(cardId => (
    items.current.some(item => String(item.cardId) === String(cardId))
  ), []);

  // Pick up anything left over from a closed tab, and retry as soon as the network is back
  useEffect(() => {
    flush();
    const online = () => { tries.current = 0; flush(); };
    window.addEventListener('online', online);
    return () => { window.removeEventListener('online', online); clearTimeout(timer.current); };
  }, [flush]);

  // Closing the tab with unsaved ratings asks for confirmation
  useEffect(() => {
    if (!pending) return undefined;
    const warn = e => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [pending]);

  return { send, retry, pending, stuck, failed, hasPendingCard };
}